import _ from "lodash";
import React from "react";
import { useAppSettingsService } from "src/services/appSettingsService/appSettingsService";
import { useDispatch, useSelector } from "src/store";
import { fetchProfile, selectProfile } from "src/store/account";
import { selectCurrentWorkspaceId, setCurrentWorkspaceId, setWorkspaceResolved } from "src/store/app";
import { selectHasValidSession } from "src/store/auth";

import { fetchGroups, fetchLicenses } from "@dashboard/devices/store";
import { Subscription } from "@dashboard/devices/types";
import { usePrefetch as usePrefetchReleases } from "@dashboard/downloads/api";
import { releaseFilesPageSize } from "@dashboard/downloads/hooks/useReleaseFiles";
import { usePrefetch as usePrefetchFiles } from "@dashboard/files/api";
import { normalizeFilesQueryParams } from "@dashboard/files/api/helpers";
import { usePrefetch as usePrefetchPolicies } from "@dashboard/policies/api";
import { productsApi, usePrefetch as usePrefetchProducts } from "@dashboard/products/api";
import { getDefaultProduct, sortProductsByName } from "@dashboard/products/utils/getDefaultProduct/getDefaultProduct";
import { fetchProvisionings } from "@dashboard/provisioning/store";
import { usePrefetch as usePrefetchFdroids } from "@dashboard/repositories/api";
import {
    useAllWorkspaces,
    useCurrentSubscriptionExpired,
    useCurrentWorkspace,
    useSubscriptions,
} from "@dashboard/workspaces/api/helpers";
import { skipToken } from "@reduxjs/toolkit/query";
import UserSettingsService from "@services/userSettingsService";

import "@services/timeService/timeService";

const licensePrefetchPageSize = 48;

export function getDefaultSubscription(subscriptions: Subscription[]) {
    const mostRecentActive = _.orderBy(
        subscriptions.filter((subscription) => subscription.status === "Active"),
        ["creationDate"],
        ["desc"],
    )[0];

    return mostRecentActive ?? subscriptions[0];
}

const usePrefetchWorkspacesQuery = () => {
    const hasValidSession = useSelector(selectHasValidSession);

    const { data: subscriptions } = useSubscriptions({}, { skip: !hasValidSession });
    const defaultSubscription = getDefaultSubscription(subscriptions);

    const {
        data: workspaces,
        isLoading: isWorkspacesLoading,
        isFetching: isWorkspacesFetching,
    } = useAllWorkspaces({}, { skip: !hasValidSession });
    const defaultWorkspace = workspaces?.find((workspace) => workspace.subscription.id === defaultSubscription?.id);

    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const {
        data: currentWorkspace,
        isLoading: isCurrentWorkspaceLoading,
        isFetching: isCurrentWorkspaceFetching,
    } = useCurrentWorkspace();

    const isWorkspaceIdWrong = workspaceId && !currentWorkspace && !isCurrentWorkspaceFetching;
    const noCurrentWorkspace = !workspaceId && !isWorkspacesFetching;

    const dispatch = useDispatch();

    React.useEffect(() => {
        if (noCurrentWorkspace && defaultWorkspace) {
            dispatch(setCurrentWorkspaceId(defaultWorkspace.id));
        } else if (noCurrentWorkspace && !defaultWorkspace) {
            dispatch(setCurrentWorkspaceId(null));
        } else if (isWorkspaceIdWrong && defaultWorkspace) {
            dispatch(setCurrentWorkspaceId(defaultWorkspace.id));
        } else if (isWorkspaceIdWrong && !defaultWorkspace) {
            dispatch(setCurrentWorkspaceId(null));
        }
    }, [isWorkspaceIdWrong, noCurrentWorkspace, defaultWorkspace, workspaceId, dispatch]);

    // Workspace resolution is pending only on the FIRST load (isLoading, i.e. no workspace data
    // yet) — not during background refetches (isFetching). A background refetch keeps the existing
    // data, so gating on isFetching here would blank the whole dashboard for its duration. This is
    // exactly what happens when creating/deleting a group invalidates the Workspaces cache: the
    // refetch would otherwise unmount the page (PrefetchGuard renders null while resolving). The
    // last term covers the gap where a default workspace is ready but its ID hasn't been written
    // to the store yet (useEffect hasn't fired).
    const isResolvingWorkspace = isWorkspacesLoading || isCurrentWorkspaceLoading || (noCurrentWorkspace && !!defaultWorkspace);

    React.useEffect(() => {
        if (!isResolvingWorkspace) {
            dispatch(setWorkspaceResolved(true));
        }
    }, [isResolvingWorkspace, dispatch]);

    return { isResolvingWorkspace };
};

const useFetchProfile = () => {
    const [progress, setProgress] = React.useState(0);
    const [hasPrefetchError, setHasPrefetchError] = React.useState(false);

    const dispatch = useDispatch();
    const hasValidSession = useSelector(selectHasValidSession);

    React.useLayoutEffect(() => {
        if (!hasValidSession) {
            return;
        }

        void dispatch(fetchProfile())
            .unwrap()
            .catch(() => {
                setHasPrefetchError(true);
            })
            .finally(() => setProgress((prev) => prev + 100));

        return () => setProgress(0);
    }, [dispatch, hasValidSession]);

    return { progress, hasPrefetchError };
};

const usePrefetchFilesQuery = () => {
    const prefetchUser = usePrefetchFiles("getFiles");

    const hasValidSession = useSelector(selectHasValidSession);
    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const { effectiveWorkspaceSettings, isLoading: isPageFlagsLoading } = useAppSettingsService();

    React.useEffect(() => {
        if (hasValidSession && workspaceId && !isPageFlagsLoading && effectiveWorkspaceSettings.isFilesPageEnabled) {
            prefetchUser(normalizeFilesQueryParams(UserSettingsService.getFilePageParams(), workspaceId));
        }
    }, [hasValidSession, workspaceId, isPageFlagsLoading, effectiveWorkspaceSettings.isFilesPageEnabled, prefetchUser]);
};

const useFetchGroups = () => {
    const [progress, setProgress] = React.useState(0);
    const [hasPrefetchError, setHasPrefetchError] = React.useState(false);

    const dispatch = useDispatch();
    const { data: workspace } = useCurrentWorkspace();
    const workspaceId = workspace?.id;

    const profile = useSelector(selectProfile);
    const isProfileCompleted = profile && profile.profileCompleted;

    React.useLayoutEffect(() => {
        if (isProfileCompleted && !workspaceId) {
            setProgress(100);
        }
        if (isProfileCompleted && workspaceId) {
            void dispatch(fetchGroups(workspaceId))
                .unwrap()
                .then(() => {
                    setProgress((prev) => prev + 50);
                    return dispatch(
                        fetchLicenses({
                            groupId: workspace.subscription.groupIds,
                            isPrefetch: true,
                            pageSize: licensePrefetchPageSize,
                        }),
                    ).unwrap();
                })
                .then((responses) => {
                    setProgress((prev) => prev + 50);
                    return dispatch(
                        fetchLicenses({
                            groupId: workspace.subscription.groupIds,
                            prefetched: { pageSize: licensePrefetchPageSize, responses },
                        }),
                    ).unwrap();
                })
                .catch(() => {
                    setHasPrefetchError(true);
                });
        }
        return () => setProgress(0);
    }, [dispatch, workspaceId, isProfileCompleted]);

    return { progress, hasPrefetchError };
};

const useFetchFdroids = () => {
    const prefetchFdroids = usePrefetchFdroids("getFdroids");
    const hasValidSession = useSelector(selectHasValidSession);
    const { data: workspace } = useCurrentWorkspace();
    const subscriptionId = workspace?.subscription.id;

    React.useLayoutEffect(() => {
        if (hasValidSession && subscriptionId) {
            prefetchFdroids({ subscriptionId });
        }
    }, [hasValidSession, subscriptionId]);
};

const useFetchProducts = () => {
    const prefetchProducts = usePrefetchProducts("fetchProducts");
    const hasValidSession = useSelector(selectHasValidSession);
    const { data: workspace } = useCurrentWorkspace();
    const workspaceId = workspace?.id;
    const isTerminated = workspace?.subscription?.status === "Terminated";

    React.useLayoutEffect(() => {
        if (hasValidSession && workspaceId) {
            prefetchProducts({ workspaceId, isTerminated });
        }
    }, [hasValidSession, workspaceId, isTerminated]);
};

const useFetchDefaultProductDetails = () => {
    const prefetchById = usePrefetchProducts("fetchProductById");
    const prefetchBuilds = usePrefetchProducts("fetchProductBuilds");
    const prefetchVariables = usePrefetchProducts("fetchProductVariables");
    const prefetchChangeLogs = usePrefetchProducts("fetchProductChangeLogs");
    const { data: workspace } = useCurrentWorkspace();
    const workspaceId = workspace?.id;
    const isTerminated = workspace?.subscription?.status === "Terminated";

    const selectProducts = React.useMemo(
        () => productsApi.endpoints.fetchProducts.select(workspaceId ? { workspaceId, isTerminated } : skipToken),
        [workspaceId, isTerminated],
    );
    const products = useSelector(selectProducts).data;
    const defaultProductId = products ? getDefaultProduct(sortProductsByName(products))?.id : undefined;

    React.useLayoutEffect(() => {
        if (defaultProductId) {
            prefetchById(defaultProductId);
            prefetchBuilds(defaultProductId);
            prefetchVariables(defaultProductId);
            prefetchChangeLogs(defaultProductId);
        }
    }, [defaultProductId]);
};

const useFetchPolicies = () => {
    const prefetchPolicies = usePrefetchPolicies("getPolicies");
    const hasValidSession = useSelector(selectHasValidSession);
    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const isSubscriptionExpired = useCurrentSubscriptionExpired();
    const { rawTenantSettings } = useAppSettingsService();
    const isPoliciesPageEnabled = rawTenantSettings.features.pages.policiesPageEnabled;

    React.useLayoutEffect(() => {
        if (hasValidSession && workspaceId && !isSubscriptionExpired && isPoliciesPageEnabled) {
            prefetchPolicies({ workspaceId });
        }
    }, [hasValidSession, workspaceId, isSubscriptionExpired, isPoliciesPageEnabled]);
};

const useFetchReleases = () => {
    const prefetchReleases = usePrefetchReleases("getReleases");
    const hasValidSession = useSelector(selectHasValidSession);
    const { data: workspace } = useCurrentWorkspace();
    const workspaceId = workspace?.id;
    const isTerminated = workspace?.subscription?.status === "Terminated";
    const { effectiveWorkspaceSettings, isLoading: isPageFlagsLoading } = useAppSettingsService();
    const needsReleases = effectiveWorkspaceSettings.isDownloadsPageEnabled || effectiveWorkspaceSettings.isEnrollmentPageEnabled;

    React.useLayoutEffect(() => {
        if (hasValidSession && workspaceId && !isTerminated && !isPageFlagsLoading && needsReleases) {
            prefetchReleases({ workspaceId, pageSize: releaseFilesPageSize });
        }
    }, [hasValidSession, workspaceId, isTerminated, isPageFlagsLoading, needsReleases]);
};

const useFetchProvisionings = () => {
    const [progress, setProgress] = React.useState(0);
    const [hasPrefetchError, setHasPrefetchError] = React.useState(false);

    const dispatch = useDispatch();
    const { data: workspace } = useCurrentWorkspace();
    const workspaceId = workspace?.id;
    const isTerminated = workspace?.subscription.status === "Terminated";
    const { effectiveWorkspaceSettings, isLoading: isPageFlagsLoading } = useAppSettingsService();

    const profile = useSelector(selectProfile);
    const isProfileCompleted = profile && profile.profileCompleted;

    React.useLayoutEffect(() => {
        if (isProfileCompleted && workspaceId && !isPageFlagsLoading && effectiveWorkspaceSettings.isProvisioningPageEnabled) {
            void dispatch(fetchProvisionings({ workspaceId: workspace.id, isTerminated }))
                .unwrap()
                .catch(() => setHasPrefetchError(true))
                .finally(() => setProgress((prev) => prev + 100));
        } else {
            setProgress(100);
        }

        return () => setProgress(0);
    }, [
        dispatch,
        workspaceId,
        isTerminated,
        isProfileCompleted,
        isPageFlagsLoading,
        effectiveWorkspaceSettings.isProvisioningPageEnabled,
    ]);

    return { progress, hasPrefetchError };
};

export const usePrefetch = () => {
    const { isResolvingWorkspace } = usePrefetchWorkspacesQuery();

    usePrefetchFilesQuery();
    useFetchFdroids();
    useFetchProducts();
    useFetchDefaultProductDetails();
    useFetchPolicies();
    useFetchReleases();

    const { progress: groupProgress, hasPrefetchError: groupError } = useFetchGroups();
    const { progress: profileProgress, hasPrefetchError: profileError } = useFetchProfile();
    const { progress: provisioningsProgress, hasPrefetchError: provisioningsError } = useFetchProvisionings();

    const stages = [groupProgress, provisioningsProgress, profileProgress];

    const hasPrefetchError = groupError || provisioningsError || profileError;

    return {
        progress: _.sum(stages) / stages.length,
        hasPrefetchError,
        isResolvingWorkspace,
    };
};
